import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Button, Flex, Grid, Layout, Spin, theme } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Header from '@/components/layout/Header';
import { PageHeader } from '@/components/ui/PageHeader';
import { ProfileIdentityCard } from '@/components/profile/ProfileIdentityCard';
import { ProfileAccountSection } from '@/components/profile/ProfileAccountSection';
import { ProfileEmailSection } from '@/components/profile/ProfileEmailSection';
import { ProfilePhoneSection } from '@/components/profile/ProfilePhoneSection';
import { ProfileSecuritySection } from '@/components/profile/ProfileSecuritySection';
import { useAuth } from '@/hooks/useAuth';
import { useProfileQuery } from '@/api/auth/queries';
import { canManageProfilePhone } from '@/utils/profilePhoneEligibility';
import { getRedirectRoute } from '@/utils/redirect';

export const Route = createFileRoute('/profile')({
  component: RouteComponent,
  beforeLoad: ({ context, location }) => {
    if (!context?.auth) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href } as { redirect?: string }
      });
    }
  }
});

function backLabelKey(backTo: string): string {
  if (backTo === '/admin') return 'profile:backToAdmin';
  if (backTo === '/manage') return 'profile:backToManage';
  if (backTo === '/client') return 'profile:backToClient';
  return 'profile:backToHome';
}

function RouteComponent() {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { t } = useTranslation(['profile']);
  const navigate = useNavigate();
  const { payload } = useAuth();
  const { data: profile, isLoading } = useProfileQuery();

  const email = profile?.email ?? payload?.email ?? '';
  const displayName = profile?.name ?? (email || t('profile:notSet'));
  const role = profile?.role ?? payload?.role ?? '';
  const canManagePhone = canManageProfilePhone(payload);
  const backTo = getRedirectRoute(payload);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Header showLogo transparent={false} logoTo={backTo} />
      <Layout.Content
        style={{
          width: '100%',
          // Phones get the standard 16px gutter; wider screens a roomier page padding.
          padding: screens.md
            ? `${token.paddingLG}px ${token.paddingXL}px ${token.paddingXL * 1.5}px`
            : `${token.padding}px ${token.padding}px ${token.paddingXL}px`,
          boxSizing: 'border-box'
        }}>
        {isLoading && !profile ? (
          <Flex justify="center" align="center" style={{ minHeight: token.controlHeight * 8 }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <div style={{ width: '100%', maxWidth: 960, margin: '0 auto' }}>
            <PageHeader
              icon={<UserOutlined />}
              title={t('profile:title')}
              description={t('profile:subtitle')}
              actions={
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate({ to: backTo })}>
                  {t(backLabelKey(backTo))}
                </Button>
              }
            />

            <Flex vertical gap={token.marginLG}>
              <ProfileIdentityCard
                profile={profile}
                email={email}
                displayName={displayName}
                role={role}
              />
              <ProfileAccountSection profile={profile} />
              <ProfileEmailSection profile={profile} />
              <ProfilePhoneSection profile={profile} canManage={canManagePhone} />
              <ProfileSecuritySection canChangePassword={profile?.canChangePassword} />
            </Flex>
          </div>
        )}
      </Layout.Content>
    </Layout>
  );
}
